import { AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { select, Store } from '@ngrx/store';
import { Observable, of, Subject } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, skip, takeUntil, tap } from 'rxjs/operators';
import { CellsConfig, DynamicTableCellType, DynamicTableData, DynamicTableRow, LazyTableDataSource } from '@rappi/ui/dynamic-table';
import { LazyScrollableDataSource } from '@rappi/common';
import { SelectComponent } from '@rappi/ui/select';
import { InputComponent } from '@rappi/ui/input';
import { AlertsType } from '@rappi/ui/alerts';

import {
  COMPANY_STATUS,
  COMPANY_STATUS_LABELS,
  COMPANY_TYPE,
  COMPANY_TYPE_LABELS,
  COMPANY_TEXT_MAX_LENGTH,
  COMPANY_MULTIPLE_OWNERS,
  COMPANY_QUERY_PARAM,
  COMPANY_QUERY_FROM,
  COMPANY_EMPTY_VALUE,
  CompanyStatus,
  CompanyStatusMeta,
  CompanyType,
} from './companies.constants';
import { CommercialOwnersDialogComponent } from '../commercial-owners-dialog/commercial-owners-dialog.component';
import { MakersFinancesMgrService } from '../../services/makers-finances-mgr.service';
import { StoreAuxService } from '../../../services/storeAux/store-aux.service';
import { AlertsService } from '../../../services/alerts/alerts.service';
import { CompanyRecord, CompanyListFilters } from '../../models/makers-finances-mgr.models';
import { AppState } from '../../../store/states/app.state';
import { StoreName } from '../../../store/definitions/store.constants';

// View-model for a single company row, mapped from the backend CompanyRecord.
interface CompanyRow {
  id: number;
  name: string;
  companyId: string;
  type: CompanyType;
  taxId: string;
  status: CompanyStatus;
  // One or more commercial owner emails. When more than one, the column shows the
  // COMPANY_MULTIPLE_OWNERS label as a link that opens the owners dialog.
  commercialOwners: string[];
  isTest: boolean;
}

interface FilterOption { id: string; name: string }

@Component({
  selector: 'app-companies',
  templateUrl: './companies.component.html',
  styleUrls: ['./companies.component.scss']
})
export class CompaniesComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('statusTemplate') statusTemplate: TemplateRef<any>;
  @ViewChild('ownerTemplate') ownerTemplate: TemplateRef<any>;
  @ViewChild('truncatedTextTemplate') truncatedTextTemplate: TemplateRef<any>;
  @ViewChild('actionsTemplate') actionsTemplate: TemplateRef<any>;

  @ViewChild('typeSelect') private readonly typeSelect: SelectComponent<FilterOption>;
  @ViewChild('statusSelect') private readonly statusSelect: SelectComponent<FilterOption>;
  @ViewChild('ownerSelect') private readonly ownerSelect: SelectComponent<FilterOption>;
  @ViewChild('searchInput') private readonly searchInput: InputComponent;

  tableData: DynamicTableData;

  // Filter selections (kept as the raw option arrays from one-select)
  filterTypes: FilterOption[] = [];
  filterStatuses: FilterOption[] = [];
  filterOwners: FilterOption[] = [];
  filterIsTest = false;
  searchTerm = '';

  // Expose constants to the template (templates cannot import constants directly).
  readonly STATUS_LABELS = COMPANY_STATUS_LABELS;
  readonly TYPE_LABELS = COMPANY_TYPE_LABELS;
  readonly TEXT_MAX_LENGTH = COMPANY_TEXT_MAX_LENGTH;
  readonly MULTIPLE_OWNERS = COMPANY_MULTIPLE_OWNERS;

  readonly selectConfig = {
    search: true,
    includeAll: true,
    showTotalsChip: false,
    maxNumberOfChips: 1,
    panelClass: 'one-select'
  };

  readonly selectConfigNoSearch = {
    ...this.selectConfig,
    search: false
  };

  typeDataSource = new LazyScrollableDataSource(this.fetchTypeOptions.bind(this));
  statusDataSource = new LazyScrollableDataSource(this.fetchStatusOptions.bind(this));
  ownerDataSource = new LazyScrollableDataSource(this.fetchOwnersOptions.bind(this));

  private readonly PAGE_SIZE = 10;
  private readonly PAGE_SIZE_OPTIONS = [10, 20, 30];
  private readonly searchSubject = new Subject<string>();
  private readonly destroy$ = new Subject<void>();
  // Guards resetTable() while we programmatically clearSelection() on the filters during a
  // country change — each clearSelection() emits selectionChanged → onXxxChange([]) → resetTable(),
  // but we only want one refetch at the end.
  private isRefreshing = false;

  constructor(
    private readonly router: Router,
    private readonly cdr: ChangeDetectorRef,
    private readonly dialog: MatDialog,
    private readonly financesService: MakersFinancesMgrService,
    private readonly storeAux: StoreAuxService,
    private readonly alertsService: AlertsService,
    private readonly store: Store<AppState>,
  ) {}

  ngOnInit(): void {
    this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      takeUntil(this.destroy$),
    ).subscribe(() => this.resetTable());

    // Refresh filters + table when the country changes — owner dropdown and the company
    // search are country-scoped. skip(1) ignores the initial value (the table's first load
    // already covers it), matching the pattern in purchase-orders.component.ts.
    this.store.pipe(
      select(StoreName.country),
      skip(1),
      takeUntil(this.destroy$),
    ).subscribe(() => this.onCountryChange());
  }

  ngAfterViewInit(): void {
    this.tableData = this.buildTableConfig();
    this.cdr.detectChanges();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onSearch(event: Event): void {
    const value = (event.target as HTMLInputElement).value.trim();
    this.searchTerm = value;
    this.searchSubject.next(value);
  }

  onTypeChange(values: FilterOption[]): void {
    this.filterTypes = values ?? [];
    this.resetTable();
  }

  onStatusChange(values: FilterOption[]): void {
    this.filterStatuses = values ?? [];
    this.resetTable();
  }

  onOwnerChange(values: FilterOption[]): void {
    this.filterOwners = values ?? [];
    this.resetTable();
  }

  onTestToggle(): void {
    this.resetTable();
  }

  // Opens the Commercial Owners dialog with the given owner emails (Figma node 1370:61735).
  openOwnersDialog(owners: string[]): void {
    this.dialog.open(CommercialOwnersDialogComponent, {
      width: '373px',
      panelClass: 'poc-dialog-panel',
      data: { owners: owners ?? [] },
    });
  }

  // Navigates to the company detail or edit view depending on the company's status.
  // Draft companies go directly to the edit form so the user can continue filling them in;
  // all other statuses go to the read-only detail view.
  viewCompany(id: number, status: CompanyStatus): void {
    const isDraft = status === COMPANY_STATUS.DRAFT;
    const queryParams = {
      [COMPANY_QUERY_PARAM.COUNTRY]: this.storeAux.country,
      ...(isDraft ? { [COMPANY_QUERY_PARAM.FROM]: COMPANY_QUERY_FROM.LIST } : {}),
    };
    if (isDraft) {
      this.router.navigate(['/financial-hub/companies', id, 'edit'], { queryParams });
    } else {
      this.router.navigate(['/financial-hub/companies', id], { queryParams });
    }
  }

  private resetTable(): void {
    if (this.isRefreshing) {
      return;
    }
    (this.tableData.dataSource as LazyTableDataSource<DynamicTableRow>).resetTable();
  }

  // Country change: clear every filter + the search box, then refetch the table once.
  private onCountryChange(): void {
    this.isRefreshing = true;

    // Clear the search box (DOM + state) without emitting a debounced resetTable.
    this.searchTerm = '';
    this.searchInput.control.setValue('');

    // clearSelection() emits selectionChanged → onXxxChange([]) → resetTable(); the guard
    // suppresses those intermediate resets so we refetch exactly once at the end.
    this.typeSelect?.clearSelection();
    this.statusSelect?.clearSelection();
    this.ownerSelect?.clearSelection();

    // Reset local filter state in case any select had no ViewChild yet (defensive).
    this.filterTypes = [];
    this.filterStatuses = [];
    this.filterOwners = [];

    // Refetch every filter's first page against the new country. setFilterValue('') clears
    // the cache (resetData) AND triggers _fetchPage(initPage), which makes the network call.
    // resetData() alone only clears the cache — the dropdown wouldn't refetch until opened.
    this.typeDataSource.setFilterValue('');
    this.statusDataSource.setFilterValue('');
    this.ownerDataSource.setFilterValue('');

    this.isRefreshing = false;
    this.resetTable();
  }

  private buildTableConfig(): DynamicTableData {
    const dataSource = new LazyTableDataSource<DynamicTableRow>(
      this.fetchCompanies.bind(this),
      this.PAGE_SIZE,
      'name',
      1
    );

    const columns = ['name', 'companyId', 'type', 'taxId', 'status', 'commercialOwner', 'actions'];

    const cellsConfig: CellsConfig = {
      name:           { type: DynamicTableCellType.templateRef, title: 'Company name' },
      companyId:      { type: DynamicTableCellType.templateRef, title: 'Company ID' },
      type:           { type: DynamicTableCellType.templateRef, title: 'Company type' },
      taxId:          { type: DynamicTableCellType.templateRef, title: 'Tax id' },
      status:         { type: DynamicTableCellType.templateRef, title: 'Status' },
      commercialOwner:{ type: DynamicTableCellType.templateRef, title: 'Commercial owner' },
      actions:        { type: DynamicTableCellType.templateRef, title: 'Details' },
    };

    return {
      dataSource,
      hideSearch: true,
      pagination: true,
      lazyLoaded: true,
      message: { title: 'No companies found' },
      paginatorOpts: { pageSize: this.PAGE_SIZE, pageSizeOptions: this.PAGE_SIZE_OPTIONS },
      columns,
      cellsConfig
    };
  }

  // Fetches a page of companies from the backend (POST /companies/list). The free-text search
  // (`q`) and the dropdown filters (statuses/types/owners) are sent in the body; the test toggle
  // is not yet backed by the endpoint and stays inert.
  private fetchCompanies(page: number, limit: number): Observable<DynamicTableRow[]> {
    return this.financesService.getCompanies(
      this.buildFilters(),
      page,
      limit,
    ).pipe(
      tap(res => {
        if (this.tableData?.paginator) {
          this.tableData.paginator.length = res.data.pagination.total_record;
        }
      }),
      map(res => res.data.records.map(record => this.toRow(this.mapRecord(record)))),
      catchError(() => {
        this.alertsService.openAlerts('Could not load companies. Please try again.', AlertsType.error);
        return of([]);
      }),
    );
  }

  // Builds the filter body for POST /companies/list from the current selections. Only present,
  // non-empty arrays are included so the backend doesn't receive empty filters.
  // `statuses` are sent UPPER_CASE (the /companies/list backend expects them that way), even though
  // the dropdown stores them as snake_case codes (to match COMPANY_STATUS for the badge).
  private buildFilters(): CompanyListFilters {
    const filters: CompanyListFilters = {};
    if (this.searchTerm) {
      filters.q = this.searchTerm;
    }
    if (this.filterStatuses.length) {
      filters.statuses = this.filterStatuses.map(s => s.id.toUpperCase());
    }
    if (this.filterTypes.length) {
      filters.types = this.filterTypes.map(t => t.id);
    }
    if (this.filterOwners.length) {
      filters.owners = this.filterOwners.map(o => o.id);
    }
    return filters;
  }

  // Resolves the status display metadata (label + cssClass) for a status code. Falls back to the
  // raw code for both when the status isn't in COMPANY_STATUS_LABELS (e.g. a new backend status not
  // yet mapped) so the badge renders without crashing — the cell shows the code as-is.
  private statusMeta(status: string): CompanyStatusMeta {
    return this.STATUS_LABELS[status as CompanyStatus] ?? { label: status, cssClass: status };
  }

  // Commercial owners: account manager + second account manager => multiple (shown in the dialog);
  // only account manager => that one replaces owner_email; otherwise falls back to owner_email.
  private resolveCommercialOwners(record: CompanyRecord): string[] {
    const accountManager = record.account_manager_email;
    const secondAccountManager = record.second_account_manager_email;
    if (accountManager && secondAccountManager) {
      return [accountManager, secondAccountManager];
    }
    const single = accountManager || record.owner_email;
    return single ? [single] : [];
  }

  // Maps a backend CompanyRecord (already normalized by the service) to the component's CompanyRow.
  // `is_test` is not part of the current endpoint response, so it defaults to false.
  private mapRecord(record: CompanyRecord): CompanyRow {
    return {
      id: record.id,
      name: record.name,
      companyId: record.maker_code,
      type: record.type_of_company as CompanyType,
      taxId: record.tax_id,
      status: record.status as CompanyStatus,
      commercialOwners: this.resolveCommercialOwners(record),
      isTest: false,
    };
  }

  private toRow(c: CompanyRow): DynamicTableRow {
    const isMultiple = c.commercialOwners.length > 1;
    const ownerRaw = isMultiple ? this.MULTIPLE_OWNERS : (c.commercialOwners[0] ?? '');
    const ownerValue = ownerRaw || COMPANY_EMPTY_VALUE;
    return {
      name:            {
        templateRef: this.truncatedTextTemplate,
        context: {
          // Coerce to string so the template's `value.length` never throws on a null backend name.
          value: c.name ?? '',
          maxLength: this.TEXT_MAX_LENGTH.name
        }
      },
      companyId:       {
        templateRef: this.truncatedTextTemplate,
        context: {
          value: c.companyId ?? '',
          maxLength: this.TEXT_MAX_LENGTH.companyId
        }
      },
      type:            {
        templateRef: this.truncatedTextTemplate,
        context: {
          // Fall back to COMPANY_EMPTY_VALUE when the backend returns a type not in TYPE_LABELS
          // (e.g. a new/unknown type_of_company code) so the cell renders '-' instead of crashing
          // the table on `value.length`.
          value: this.TYPE_LABELS[c.type] ?? COMPANY_EMPTY_VALUE,
          maxLength: this.TEXT_MAX_LENGTH.type
        }
      },
      taxId:           {
        templateRef: this.truncatedTextTemplate,
        context: {
          value: c.taxId || COMPANY_EMPTY_VALUE,
          maxLength: this.TEXT_MAX_LENGTH.taxId
        }
      },
      status:          {
        templateRef: this.statusTemplate,
        context: {
          label: this.statusMeta(c.status).label,
          code: this.statusMeta(c.status).cssClass
        }
      },
      commercialOwner: {
        templateRef: this.ownerTemplate,
        context: {
          value: ownerValue, owners: c.commercialOwners,
          maxLength: this.TEXT_MAX_LENGTH.commercialOwner
        }
      },
      actions:         { templateRef: this.actionsTemplate, context: { id: c.id, status: c.status } },
    };
  }

  private fetchOwnersOptions(page = 0, limit = 20, search = ''): Observable<FilterOption[]> {
    return this.financesService.searchOwners(this.storeAux.country, page + 1, limit, search || undefined).pipe(
      map(res => res.data.records.map(o => ({ id: o.email, name: o.email }))),
      catchError(() => of([]))
    );
  }

  // ── Filter option fetchers ──────────────────────────────────────────────────
  // Company type options come from GET /company-type-options; commercial owners from the same
  // searchOwners endpoint used by the purchase-orders table; status is a static local list.

  private fetchTypeOptions(page = 0, limit = 20): Observable<FilterOption[]> {
    return this.financesService.getCompanyTypeOptions(page + 1, limit).pipe(
      // `id` is the snake_case code so it can be sent directly as the `types` filter to the backend.
      map(res => res.data.records.map(r => ({ id: r.code, name: r.name }))),
      catchError(() => of([])),
    );
  }

  private fetchStatusOptions(page = 0): Observable<FilterOption[]> {
    // The statuses endpoint returns a flat array (no pagination), so return all items on the first
    // page and nothing afterwards so LazyScrollableDataSource doesn't concatenate duplicates.
    if (page > 0) {
      return of([]);
    }
    return this.financesService.getCompanyStatuses().pipe(
      // `id` is the snake_case code (normalized by the service) so it can be sent directly as the
      // `statuses` filter and matches COMPANY_STATUS for the badge.
      map(res => res.data.map(s => ({ id: s.code, name: s.label }))),
      catchError(() => of([])),
    );
  }
}
